import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/efs/PageHeader';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import type { ApiClientError } from '@/lib/api-client';

import {
  useDkConfirmEfDkSettlement,
  useEfDkSettlements,
  useFinalConfirmEfDkSettlement,
  useLockEfDkSettlement,
  useRecomputeEfDkSettlement,
  useReviseEfDkSettlement,
} from './useEfDkSettlement';
import type { EfDkSettlementDto, EfDkSettlementStatus } from './types';

const NS = 'settlement';

/** Format a decimal money string (JSON string from the backend). */
const formatAmount = (value: string | null): string => {
  if (value == null || value === '') return '-';
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const statusToneClass: Record<EfDkSettlementStatus, string> = {
  draft: 'bg-efs-bg-subtle text-efs-text-muted',
  calculated: 'bg-blue-50 text-blue-900',
  dk_confirmed: 'bg-amber-50 text-amber-900',
  final_confirmed: 'bg-emerald-50 text-emerald-900',
  locked: 'bg-slate-800 text-slate-50',
};

const StatusBadge = ({ status }: { status: EfDkSettlementStatus }) => {
  const { t } = useTranslation(NS);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium uppercase tracking-wide',
        statusToneClass[status],
      )}
    >
      {t(`efs.settlement.ef_dk.status.${status}`, { defaultValue: status })}
    </span>
  );
};

/** One A/B/C/D-style metric row in the summary card. */
const MetricRow = ({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string | null;
  emphasize?: boolean;
}) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className="text-sm text-efs-text-muted">{label}</span>
    <span
      className={cn(
        'font-mono tabular-nums',
        emphasize ? 'text-lg font-semibold text-efs-text-primary' : 'text-sm',
      )}
    >
      {formatAmount(value)}
    </span>
  </div>
);

const ReviseDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
}) => {
  const { t } = useTranslation(NS);
  const [reason, setReason] = useState('');

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setReason('');
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('efs.settlement.ef_dk.revise.title', { defaultValue: '修正依頼' })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.settlement.ef_dk.revise.subtitle', {
              defaultValue: '確定済みの精算を差し戻し、再計算できる状態に戻します。',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium text-efs-text-primary" htmlFor="revise-reason">
            {t('efs.settlement.ef_dk.revise.reason_label', { defaultValue: '修正理由' })}
          </label>
          <Textarea
            id="revise-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('efs.settlement.ef_dk.revise.reason_placeholder', {
              defaultValue: '修正が必要な理由を入力してください',
            })}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isPending || reason.trim() === ''}>
            {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('efs.settlement.ef_dk.actions.revise', { defaultValue: '修正依頼' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SettlementCard = ({ settlement }: { settlement: EfDkSettlementDto }) => {
  const { t } = useTranslation(NS);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>
              {t('efs.settlement.ef_dk.summary.title', { defaultValue: '精算サマリー' })}
            </CardTitle>
            <CardDescription>
              {t('efs.settlement.ef_dk.summary.subtitle', {
                defaultValue: '仕入価格の利益基準Dから精算金額を算定します。',
              })}
            </CardDescription>
          </div>
          <StatusBadge status={settlement.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
          <div className="divide-y divide-efs-border">
            <MetricRow
              label={t('efs.settlement.ef_dk.fields.value_a', { defaultValue: 'A 月次素材総額' })}
              value={settlement.value_a}
            />
            <MetricRow
              label={t('efs.settlement.ef_dk.fields.value_b', { defaultValue: 'B 一次査定額' })}
              value={settlement.value_b}
            />
            <MetricRow
              label={t('efs.settlement.ef_dk.fields.value_c', { defaultValue: 'C 運賃' })}
              value={settlement.value_c}
            />
            <MetricRow
              label={t('efs.settlement.ef_dk.fields.value_d', {
                defaultValue: 'D 利益基準 (A−B−C)',
              })}
              value={settlement.value_d}
              emphasize
            />
          </div>
          <div className="divide-y divide-efs-border">
            <MetricRow
              label={t('efs.settlement.ef_dk.fields.dk_share', { defaultValue: 'DK 配分' })}
              value={settlement.dk_share}
            />
            <MetricRow
              label={t('efs.settlement.ef_dk.fields.ef_share', { defaultValue: 'EF 配分' })}
              value={settlement.ef_share}
            />
            <MetricRow
              label={t('efs.settlement.ef_dk.fields.ef_to_dk_settlement_price', {
                defaultValue: 'EF→DK 精算価格',
              })}
              value={settlement.ef_to_dk_settlement_price}
              emphasize
            />
          </div>
        </div>

        <dl className="grid gap-x-8 gap-y-1 text-xs text-efs-text-muted sm:grid-cols-3">
          <div className="flex justify-between gap-2">
            <dt>{t('efs.settlement.ef_dk.fields.dk_confirmed_at', { defaultValue: 'DK確認' })}</dt>
            <dd>{formatDateTime(settlement.dk_confirmed_at)}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt>
              {t('efs.settlement.ef_dk.fields.final_confirmed_at', { defaultValue: '最終確認' })}
            </dt>
            <dd>{formatDateTime(settlement.final_confirmed_at)}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt>{t('efs.settlement.ef_dk.fields.locked_at', { defaultValue: 'ロック' })}</dt>
            <dd>{formatDateTime(settlement.locked_at)}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
};

export const EfDkSettlementPage = () => {
  const { t } = useTranslation(NS);
  const { batchId } = useParams();
  const user = useCurrentUser();

  const canCompute = hasPermission(user, 'valuation.read');
  const canDkConfirm = hasPermission(user, 'settlement.dk.confirm');
  const canFinalConfirm = hasPermission(user, 'settlement.final_confirm');
  const canLock = hasPermission(user, 'settlement.lock');
  const canRevise = hasPermission(user, 'settlement.revise');

  const [reviseOpen, setReviseOpen] = useState(false);

  const query = useEfDkSettlements(batchId);
  const recompute = useRecomputeEfDkSettlement(batchId);
  const dkConfirm = useDkConfirmEfDkSettlement(batchId);
  const finalConfirm = useFinalConfirmEfDkSettlement(batchId);
  const lock = useLockEfDkSettlement(batchId);
  const revise = useReviseEfDkSettlement(batchId);

  // The batch carries at most one active settlement header; use the latest.
  const settlement = query.data?.[0];
  const status = settlement?.status;

  // Surface the most recent action error (e.g. backend Conflict message).
  const actionError: ApiClientError | null =
    recompute.error ?? dkConfirm.error ?? finalConfirm.error ?? lock.error ?? revise.error ?? null;

  const anyPending =
    recompute.isPending ||
    dkConfirm.isPending ||
    finalConfirm.isPending ||
    lock.isPending ||
    revise.isPending;

  const handleRevise = async () => {
    if (!settlement) return;
    await revise.mutateAsync(settlement.ef_dk_settlement_id);
    setReviseOpen(false);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settlement.ef_dk.title', { defaultValue: '仕入先精算' })}
        subtitle={t('efs.settlement.ef_dk.subtitle', {
          defaultValue: '利益基準Dから仕入先精算価格を算定します',
        })}
        actions={
          canCompute && batchId ? (
            <Button
              variant="outline"
              onClick={() => recompute.mutate()}
              disabled={anyPending || status === 'locked'}
            >
              {recompute.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-1 h-4 w-4" />
              )}
              {t('efs.settlement.ef_dk.actions.recompute', { defaultValue: '再計算' })}
            </Button>
          ) : null
        }
      />

      {actionError ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {t('efs.settlement.ef_dk.error.title', { defaultValue: '操作できませんでした' })}
          </AlertTitle>
          <AlertDescription>{actionError.message}</AlertDescription>
        </Alert>
      ) : null}

      {settlement?.is_negative_d ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {t('efs.settlement.ef_dk.negative_d.title', { defaultValue: 'D がマイナスです' })}
          </AlertTitle>
          <AlertDescription>
            {t('efs.settlement.ef_dk.negative_d.description', {
              defaultValue: '利益基準 D が負の値です。DK確認と最終確認の二重確認が必須です。',
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <div className="flex items-center gap-2 py-12 text-sm text-efs-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('efs.common.actions.loading', { ns: 'common', defaultValue: '読み込み中…' })}
        </div>
      ) : settlement ? (
        <>
          <SettlementCard settlement={settlement} />

          <div className="flex flex-wrap items-center gap-2">
            {canDkConfirm ? (
              <Button
                variant="outline"
                onClick={() => dkConfirm.mutate(settlement.ef_dk_settlement_id)}
                disabled={anyPending || status !== 'calculated'}
              >
                {dkConfirm.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.settlement.ef_dk.actions.dk_confirm', { defaultValue: 'DK確認' })}
              </Button>
            ) : null}
            {canFinalConfirm ? (
              <Button
                variant="outline"
                onClick={() => finalConfirm.mutate(settlement.ef_dk_settlement_id)}
                disabled={anyPending || status !== 'dk_confirmed'}
              >
                {finalConfirm.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.settlement.ef_dk.actions.final_confirm', { defaultValue: '最終確認' })}
              </Button>
            ) : null}
            {canLock ? (
              <Button
                onClick={() => lock.mutate(settlement.ef_dk_settlement_id)}
                disabled={anyPending || status !== 'final_confirmed'}
              >
                {lock.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.settlement.ef_dk.actions.lock', { defaultValue: 'ロック' })}
              </Button>
            ) : null}
            {canRevise ? (
              <Button
                variant="ghost"
                onClick={() => setReviseOpen(true)}
                disabled={anyPending || status === 'locked'}
              >
                {t('efs.settlement.ef_dk.actions.revise', { defaultValue: '修正依頼' })}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-sm text-efs-text-muted">
            {canCompute
              ? t('efs.settlement.ef_dk.empty.computable', {
                  defaultValue: 'まだ精算がありません。「再計算」で作成してください。',
                })
              : t('efs.settlement.ef_dk.empty.read_only', {
                  defaultValue: 'まだ精算がありません。',
                })}
          </CardContent>
        </Card>
      )}

      <ReviseDialog
        open={reviseOpen}
        onOpenChange={setReviseOpen}
        onConfirm={handleRevise}
        isPending={revise.isPending}
      />
    </div>
  );
};

export default EfDkSettlementPage;
